const API_URL = process.env.REACT_APP_API_URL || "https://api.bongogames.online/api";

function getToken() {
  return localStorage.getItem("token");
}

function setToken(token) {
  if (token) {
    localStorage.setItem("token", token);
  } else {
    localStorage.removeItem("token");
  }
}

async function request(path, options = {}) {
  const headers = {};

  if (!(options.body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }
  headers["Accept"] = "application/json";

  const token = getToken();
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: { ...headers, ...options.headers },
  });

  const contentType = res.headers.get("content-type") || "";
  const contentLength = res.headers.get("content-length");
  const isJson = contentType.includes("application/json");

  let data = null;
  if (res.status !== 204 && (!contentLength || contentLength !== "0")) {
    try {
      data = isJson ? await res.json() : await res.text();
    } catch (e) {
      data = null;
    }
  }

  if (!res.ok) {
    const message = data?.message || data || `HTTP ${res.status} error`;
    throw new Error(message);
  }

  return data;
}

export const api = {
  get(path) {
    return request(path);
  },
  post(path, body) {
    return request(path, {
      method: "POST",
      body: body instanceof FormData ? body : JSON.stringify(body),
    });
  },
  put(path, body) {
    return request(path, {
      method: "PUT",
      body: body instanceof FormData ? body : JSON.stringify(body),
    });
  },
  delete(path) {
    return request(path, { method: "DELETE" });
  },
  upload(path, formData, onProgress) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${API_URL}${path}`);

      const token = getToken();
      if (token) {
        xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      }
      xhr.setRequestHeader("Accept", "application/json");

      if (onProgress) {
        xhr.upload.addEventListener("progress", (e) => {
          if (e.lengthComputable) {
            onProgress(Math.round((e.loaded / e.total) * 100));
          }
        });
      }

      xhr.onload = () => {
        const contentType = xhr.getResponseHeader("content-type") || "";
        const isJson = contentType.includes("application/json");
        let data = null;
        try {
          data = isJson ? JSON.parse(xhr.responseText) : xhr.responseText;
        } catch (e) {
          data = null;
        }
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(data);
        } else {
          const message = data?.message || data || `HTTP ${xhr.status} error`;
          reject(new Error(message));
        }
      };

      xhr.onerror = () => reject(new Error("Network error"));
      xhr.ontimeout = () => reject(new Error("Upload timed out"));
      xhr.timeout = 600000;
      xhr.send(formData);
    });
  },
};

export const auth = {
  async login(username, password) {
    const data = await api.post("/auth/login", { username, password });
    setToken(data.token);
    return data.user;
  },
  async register(username, phone, password) {
    const data = await api.post("/auth/register", { username, phone, password });
    setToken(data.token);
    return data.user;
  },
  async logout() {
    await api.post("/auth/logout");
    setToken(null);
  },
  async me() {
    const data = await api.get("/auth/me");
    return data.user;
  },
  async forgotPassword(username, phone) {
    return api.post("/auth/forgot-password", { username, phone });
  },
  async googleLogin(firebaseToken) {
    const data = await api.post("/auth/google", { token: firebaseToken });
    setToken(data.token);
    return { user: data.user, token: data.token };
  },
  async resetPassword(username, phone, password, passwordConfirmation) {
    return api.post("/auth/reset-password", {
      username,
      phone,
      password,
      password_confirmation: passwordConfirmation,
    });
  },
  getToken,
  isLoggedIn() {
    return !!getToken();
  },
};

export function storageUrl(path) {
  if (!path) return "https://placehold.co/400x300/132347/d4af37?text=BongoGames";
  return `https://api.bongogames.online/storage/${path}`;
}
